
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { cn } from '@/lib/utils';

interface WizardSummaryProps {
  data: {
    provider?: any;
    user?: any;
    leadAccount?: any;
    strategy?: any;
    account?: any;
  };
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const WizardSummary = ({ data, onSubmit, isSubmitting }: WizardSummaryProps) => {
  const hasAllData = data.provider && data.user && data.leadAccount && data.strategy && data.account;
  
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-medium">Review Provider Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Please verify all information before creating the provider.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SummaryCard
          title="Provider Information"
          isComplete={!!data.provider?.name}
          data={[
            { label: "Provider Name", value: data.provider?.name || "Not set" },
            { label: "Website", value: data.provider?.website || "N/A" },
            { label: "Status", value: data.provider?.isActive ? "Active" : "Inactive" },
            { label: "Description", value: data.provider?.description || "N/A" },
          ]}
        />
        
        <SummaryCard
          title="User Assignment"
          isComplete={!!data.user?.id || (!!data.user?.email && !!data.user?.firstName)}
          data={data.user?.assignmentType === 'existing' ? [
            { label: "Assignment Type", value: "Existing User" },
            { label: "User ID", value: data.user?.id || "Not selected" },
          ] : [
            { label: "Assignment Type", value: "New User" },
            { label: "Email", value: data.user?.email || "Not set" },
            { label: "Name", value: data.user?.firstName && data.user?.lastName ? 
              `${data.user.firstName} ${data.user.lastName}` : "Not set" },
          ]}
        />
        
        <SummaryCard
          title="Lead Account"
          isComplete={!!data.leadAccount?.externalId && !!data.leadAccount?.exchange}
          data={[
            { label: "External ID", value: data.leadAccount?.externalId || "Not set" },
            { label: "Exchange", value: data.leadAccount?.exchange || "Not selected" },
            { label: "Status", value: data.leadAccount?.isActive ? "Active" : "Inactive" },
            { label: "API Configured", value: data.leadAccount?.apiKey ? "Yes" : "No" },
          ]}
        />
        
        <SummaryCard
          title="Strategy"
          isComplete={!!data.strategy?.name}
          data={[
            { label: "Strategy Name", value: data.strategy?.name || "Not set" },
            { label: "Exchange", value: data.strategy?.exchange || "Not selected" },
            { label: "Currency", value: data.strategy?.currency || "Not selected" },
            { label: "Risk Level", value: capitalizeFirstLetter(data.strategy?.riskLevel) || "Not selected" },
            { label: "Min Investment", value: data.strategy?.minInvestment ? 
              `${data.strategy.minInvestment} ${data.strategy.currency || ""}` : "Not set" },
            { label: "Plan Type", value: capitalizeFirstLetter(data.strategy?.planType) || "Not selected" },
          ]}
        />
        
        <SummaryCard
          title="Trading Account"
          isComplete={!!data.account?.externalId}
          data={[
            { label: "External ID", value: data.account?.externalId || "Not set" },
            { label: "Performance URL", value: data.account?.performanceUrl || "N/A" },
            { label: "Primary Account", value: data.account?.isPrimary ? "Yes" : "No" },
            { label: "Status", value: data.account?.isActive ? "Active" : "Inactive" },
          ]}
        />
      </div>

      {!hasAllData && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium">Some information is missing</h4>
            <p className="text-sm">Please complete all steps of the wizard before creating the provider.</p>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex justify-end">
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || !hasAllData}
          size="lg"
          className="min-w-[200px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Provider...
            </>
          ) : (
            'Create Provider'
          )}
        </Button>
      </div>
    </div>
  );
};

// Helper component for displaying each summary card
const SummaryCard = ({ 
  title, 
  isComplete, 
  data 
}: { 
  title: string; 
  isComplete: boolean; 
  data: { label: string; value: string }[] 
}) => {
  return (
    <Card className={cn(
      "border",
      isComplete ? "bg-card" : "bg-muted/20"
    )}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex justify-between items-center">
          {title}
          {isComplete && (
            <Check className="h-5 w-5 text-green-500" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-4">
        <dl className="space-y-2">
          {data.map((item, index) => (
            <div key={index} className="flex justify-between text-sm">
              <dt className="text-muted-foreground font-medium">{item.label}:</dt>
              <dd className="font-medium">{item.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string?: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};
